import { useEffect, useRef, useState } from 'react';
import { loadGsap } from '../../../shared/lib/gsap';
import { useMotionReady } from './useMotionReady';
import { useNearViewport } from './useNearViewport';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';

type UseCareJourneyOptions = {
  chapterCount: number;
  onProgress?: (progress: number) => void;
};

export function useCareJourney({ chapterCount, onProgress }: UseCareJourneyOptions) {
  const sectionRef = useRef<HTMLElement>(null);
  const pinRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const [activeChapter, setActiveChapter] = useState(0);
  const prefersReducedMotion = usePrefersReducedMotion();
  const near = useNearViewport(sectionRef);
  const motionReady = useMotionReady();
  const progressHandler = useRef(onProgress);

  useEffect(() => {
    progressHandler.current = onProgress;
  }, [onProgress]);

  useEffect(() => {
    if (prefersReducedMotion || !near || !motionReady) {
      return;
    }

    const section = sectionRef.current;
    const pin = pinRef.current;

    if (!section || !pin) {
      return;
    }

    let cancelled = false;
    let timeline: gsap.core.Timeline | null = null;

    void loadGsap().then(({ gsap }) => {
      if (cancelled) {
        return;
      }

      // Scroll position only. The chapter copy is React's to animate - it sets
      // opacity and transform inline on every render, so a GSAP tween over the
      // same two properties was overwritten on the next commit and never
      // rendered. It still built three tweens and evaluated them on every
      // scrub frame.
      timeline = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: () => `+=${window.innerHeight * (chapterCount - 0.5)}`,
          pin,
          scrub: 0.6,
          anticipatePin: 1,
          onUpdate: (self) => {
            const progress = self.progress;
            const index = Math.min(chapterCount - 1, Math.floor(progress * chapterCount));
            setActiveChapter((current) => (current === index ? current : index));
            progressHandler.current?.(progress);
          },
        },
      });
    });

    return () => {
      cancelled = true;
      timeline?.scrollTrigger?.kill();
      timeline?.kill();
    };
  }, [chapterCount, motionReady, near, prefersReducedMotion]);

  return {
    sectionRef,
    pinRef,
    copyRef,
    activeChapter: prefersReducedMotion ? 0 : activeChapter,
    prefersReducedMotion,
  };
}
